import axios from "axios";
import { getBaseURL } from "utils/HTTPUtils/http-utils";
import { EnvironmentVariable } from "constants/EnvironmentVariables";
import { AuthSessionStorageKeys } from "../navigation/config";
import { decodeJWTToken } from "utils/HTTPUtils/session-service";
import { JwtPayload } from "jwt-decode";
import { AppConstant } from "constants/AppConstant";

const { idamConfig } = EnvironmentVariable;

interface AuthObject {
  access_token: string;
  refresh_token: string;
  id_token: string;
  scope: "openid";
  token_type: "Bearer";
  expires_in: number;
}

function base64UrlEncode(array: Uint8Array): string {
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

export async function generateCodeVerifier(): Promise<string> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

export async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(digest));
}

export async function getIDAMAccessToken(code: string) {
  try {
    const baseURL = getBaseURL(idamConfig.getAccessTokenURL);
    const clientId = getBaseURL(idamConfig.clientId);
    const redirectURL = `${getBaseURL(idamConfig.appURL)}/auth`;
    const params = new URLSearchParams();
    params.append("client_id", clientId ?? "");
    params.append("redirect_uri", redirectURL ?? "");
    params.append("code", code);
    params.append("grant_type", "authorization_code");

    const codeVerifier = sessionStorage.getItem(AuthSessionStorageKeys.codeVerifier);
    if (codeVerifier) {
      params.append("code_verifier", codeVerifier);
      sessionStorage.removeItem(AuthSessionStorageKeys.codeVerifier);
    }

    const response = await axios.post(baseURL, params);
    return response.data;
  } catch (err) {
    console.error("Unable to get a token.");
    return;
  }
}

export async function logoutLocally(): Promise<void> {
  const accessToken = sessionStorage.getItem(AuthSessionStorageKeys.accessToken);

  try {
    if (accessToken) {
      const baseURL = getBaseURL(EnvironmentVariable.localEnvConfig.baseAPIURL);
      await axios.post(
        `${baseURL}/auth/logout`,
        undefined,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          timeout: 3000,
        },
      );
    }
  } catch (error) {
    console.warn("Unable to blocklist the access token during logout", error);
  } finally {
    clearAuthDataFromSessionStorage();
  }
}

export function isTokenExpired(idToken: string): boolean {
  const decodedToken: JwtPayload = decodeJWTToken(idToken);
  const currentTime = Math.floor(Date.now() / 1000);

  if (decodedToken && decodedToken.exp) {
    return decodedToken.exp <= currentTime;
  }

  return true;
}

export const clearAuthDataFromSessionStorage = (): void => {
  sessionStorage.removeItem(AuthSessionStorageKeys.accessToken);
  sessionStorage.removeItem(AuthSessionStorageKeys.userInfo);
  sessionStorage.removeItem(AuthSessionStorageKeys.idToken);
  sessionStorage.removeItem(AuthSessionStorageKeys.refreshToken);
  sessionStorage.removeItem(AuthSessionStorageKeys.codeVerifier);
  localStorage.removeItem(AppConstant.localStorageKeys.dismissedBannerMessage);
  localStorage.removeItem(
    AppConstant.localStorageKeys.dismissedMacroNotification,
  );
};

export const handleAddAuthObjectToSessionStorage = (
  authObject: AuthObject,
): void => {
  if (authObject) {
    clearAuthDataFromSessionStorage();
    sessionStorage.setItem(
      AuthSessionStorageKeys.accessToken,
      authObject.access_token,
    );
    sessionStorage.setItem(AuthSessionStorageKeys.idToken, authObject.id_token);
    sessionStorage.setItem(
      AuthSessionStorageKeys.refreshToken,
      authObject.refresh_token,
    );
    const userInfo = decodeJWTToken(authObject.id_token);
    if (userInfo) {
      sessionStorage.setItem(
        AuthSessionStorageKeys.userInfo,
        JSON.stringify(userInfo),
      );
    }
  }
};
